/*
 * Copyright (c) Nexign, JSC, 2022-2023.
 */
/**
 * Account Info EDM Delivery Component
 *
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.22.0
 */

import { useEffect, type FC } from 'react';

import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { icons } from '@assets/index';
import { LinkBlock } from '@components/common';
import { ImgStyled } from '@components/common/Block/Img';
import { FormBlockStyled, FormLabelHintStyled } from '@components/common/FormStyled/FormStyled';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { EdmConnectionStatus } from '@modules/edmDelivery/components/EdmConnectionStatus';
import { EdmOperatorConnectionModal } from '@modules/edmDelivery/components/EdmOperatorConnectionModal';
import { openEdmInquiry } from '@modules/edmDelivery/util/openEdmInquiryModal';
import { setEdmDisabled } from '@modules/edmDelivery/util/setEdmDisabled';
import { setEdmTooltipTitle } from '@modules/edmDelivery/util/setTooltipTitle';
import FormFieldCheckbox from '@modules/form/components/FormFieldCheckbox';
import PageLocale from '@modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '@modules/internationalization/constants';
import { makeSelectEdmOperatorConnectionTranslation } from '@modules/internationalization/intSelectors';
import { modalOpen } from '@modules/modal/modalActions';
import ServiceUserDenied from '@modules/role/components/ServiceUserDenied';
import { CheckboxServiceUserDeniedStyled } from '@modules/role/components/styled/CheckboxServiceUserDeniedStyled';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectGuardComponentEnabled } from '@modules/role/selectors/guardSelector';
import { AuthorityRoleEnum } from '@pages/PageAuth/constants/authConstants';
import {
    makeSelectAuthoritiesIsServiceUser,
    makeSelectUserIsCoordinator,
} from '@pages/PageAuth/selectors/authSelectors';
import type { IRouteParams } from '@routes/constants/routesConstants';
import { useCheckCustomRole } from '@utils/useCheckCustomRole';

import { AccountInfoDeliveryFields } from '../../pageAccountInfoDeliveryConstant';
import { useSendEdmDeliveryViewedMutation } from '../../store';
import { ClientId } from '../../types';
import type { IAccountDeliveryInfoData, IAccountInfoDeliveryData } from '../../types/pageAccountInfoTypes';
import { AccountDeliveryTypeStyled, EdmSettingsInfoStyled } from '../styled/AccountInfoDeliveryStyled';

interface IProps {
    deliveryMeta: IAccountDeliveryInfoData;
}

export const AccountDeliveryEdm: FC<IProps> = ({ deliveryMeta }) => {
    const dispatch = useDispatch();

    const [sendEdmDeliveryViewed] = useSendEdmDeliveryViewedMutation();

    const { values } = useFormikContext<IAccountInfoDeliveryData>();

    const { clientId } = useParams<IRouteParams>();

    const tEdm = useSelector(makeSelectEdmOperatorConnectionTranslation());
    const isCoordinator = useSelector(makeSelectUserIsCoordinator());
    const isServiceUser = useSelector(makeSelectAuthoritiesIsServiceUser());
    const isInquiriesEnabled = useSelector(makeSelectGuardComponentEnabled(AccessComponentKeyEnum.inquiries));
    const isCustomRoleCheckAvailable = useCheckCustomRole(false, AuthorityRoleEnum.ROLE_DOCS_MANAGE);

    const isCheckboxEnabled = isCoordinator || isCustomRoleCheckAvailable;

    const isConnectButtonVisible =
        isCheckboxEnabled && deliveryMeta.edm?.isConnectVisible && !deliveryMeta.edm?.connections.length;

    const isEdmDisable = setEdmDisabled(isServiceUser, isCoordinator, isCustomRoleCheckAvailable, values, deliveryMeta);

    const handleConnectEdmOperator = () => {
        dispatch(
            modalOpen({
                modalParams: {
                    isCustomCrossButton: true,
                },
                render: ({ onCloseModal }) => (
                    <EdmOperatorConnectionModal
                        clientId={clientId}
                        macroregionName={deliveryMeta.edm?.macroregionName}
                        onCloseModal={onCloseModal}
                    />
                ),
            }),
        );
    };

    const handleOpenEdmInquiry = () => {
        openEdmInquiry(
            isInquiriesEnabled,
            isCheckboxEnabled,
            deliveryMeta.edm?.inquiryCode,
            dispatch,
            clientId,
            tEdm.connectionStatus.inquiryError,
        );
    };

    useEffect(() => {
        sendEdmDeliveryViewed({ clientId });
    }, [clientId, sendEdmDeliveryViewed]);

    return (
        <>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.edmOperatorConnection]} />
            <AccountDeliveryTypeStyled isConnectButtonVisible={isConnectButtonVisible}
                                       withInfoBlock={isCheckboxEnabled}>
                <div className="delivery__edm-field">
                    <FormBlockStyled className="delivery__address-field-checkbox">
                        <ServiceUserDenied
                            renderComponent={() => (
                                <ArrowTooltip
                                    disableHoverListener={isServiceUser}
                                    maxWidth="320px"
                                    title={
                                        setEdmTooltipTitle(isCheckboxEnabled, isEdmDisable, deliveryMeta, values, tEdm)}
                                    titleLink={values.usePost ? tEdm.inquiry : ''}
                                    onTitleLinkClick={handleOpenEdmInquiry}
                                >
                                    <CheckboxServiceUserDeniedStyled
                                        data-name="edm-checkbox-container"
                                        disabled={isEdmDisable}
                                    >
                                        <FormFieldCheckbox
                                            dataName="delivery-use-edm"
                                            disabled={isEdmDisable}
                                            label={tEdm.useEdm}
                                            name={AccountInfoDeliveryFields.useEdm}
                                        />
                                    </CheckboxServiceUserDeniedStyled>
                                </ArrowTooltip>
                            )}
                            />
                            <FormLabelHintStyled className="close-icon-label">
                                <ArrowTooltip maxWidth="320px" title={tEdm.edmInfoTooltipTitle} titleAlign="left">
                                    <ImgStyled className="small" src={icons.infoBlueIcon} />
                                </ArrowTooltip>
                            </FormLabelHintStyled>
                    </FormBlockStyled>
                    {isConnectButtonVisible && (
                        <>
                            <EdmSettingsInfoStyled>{tEdm.edmInfoMessage}</EdmSettingsInfoStyled>
                            <LinkBlock dataName="connectOperator" onClick={handleConnectEdmOperator}>
                                {tEdm.connectOperator}
                            </LinkBlock>
                        </>
                    )}
                    {!!deliveryMeta.edm?.connections.length ? (
                        <EdmConnectionStatus clientId={ClientId(clientId)} edm={deliveryMeta.edm} />
                    ) : null}
                </div>
            </AccountDeliveryTypeStyled>
        </>
    );
};
